import { Action, createReducer, on } from '@ngrx/store';

import { isEmpty } from '@app/util/util';
import * as userHistoryActions from '@user-history/store/actions/user-history.actions';
import * as flightsResultsActions from '@flight-results/store/actions/flights-results.actions';
import * as hotelsActions from '@hotels/hotel-results/store/actions/hotels.actions';
import * as hotelDetailActions from '@hotels/hotel-detail/store/actions/hotels.actions';
import * as busResultsActions from '@buses/bus-results/store/actions/bus-results.actions';
import * as autoActions from '@autos/auto-results/store/actions/auto.actions';
import * as packagesResultActions from '@packages/packages-result/store/actions/packages-result.actions';
import * as upsellActions from '@upsell/store/actions/upsell.actions';
import * as autoDetailActions from '@autos/auto-detail/store/actions/auto.actions';
import * as activitiesResultsActions from '@activities/activities-results/store/actions/activities-results.actions';
import { searchErrorAction } from '@app/store/actions/global.actions';
import {
  ErrorHandle, NoResults, NoResultsTypes, NO_PAST_DATES, ResultsErrorTypes
} from '@shared/no-results/entities/results-errors';

export const initialState = {
  error: null,
  status: false,
  uiError: null,
};

const featureReducer = createReducer(
  initialState,
  on(
    userHistoryActions.exchangeSearchAction,
    flightsResultsActions.searchFlightsAction,
    flightsResultsActions.clearFlightResultsAction,
    hotelsActions.searchHotelsAction,
    hotelsActions.clearHotelResultsAction,
    busResultsActions.searchBusesAction,
    busResultsActions.clearBusesResultsAction,
    autoActions.searchAutoAction,
    autoActions.clearAutoResultsAction,
    packagesResultActions.searchPackageAction,
    packagesResultActions.searchPackageFlightsForMobileAction,
    hotelDetailActions.fetchHotelDetailAction,
    activitiesResultsActions.cleanActivitiesResultsAction,
    () => initialState
  ),
  on(
    packagesResultActions.searchPackageHotelsWithFiltersAction,
    packagesResultActions.searchPackageFlightsForDesktopAction,
    (state, { block }) => block ? state : initialState),
  on(
    flightsResultsActions.searchFlightsForMobileErrorAction,
    flightsResultsActions.searchFlightsForDesktopErrorAction,
    upsellActions.fetchUpsellOptionsErrorBySectorAction,
    hotelsActions.searchHotelsErrorAction,
    busResultsActions.searchBusesForMobileErrorAction,
    busResultsActions.searchBusesForDesktopErrorAction,
    autoActions.searchAutoErrorAction,
    autoActions.searchAutoCategoriesErrorAction,
    packagesResultActions.searchPackageHotelsErrorAction,
    packagesResultActions.searchPackageFlightsForMobileErrorAction,
    packagesResultActions.searchPackageFlightsForDesktopErrorAction,
    hotelDetailActions.fetchHotelDetailDeeplinkErrorAction,
    autoDetailActions.fetchAutoDetailErrorAction,
    searchErrorAction,
    (state, { payload }) => {
      const errorHandle = typeof payload.error === 'string' ? {
        ...initialState,
        error: { [ResultsErrorTypes.Message]: payload.error },
        status: true
      } :
        {
          ...initialState,
          error: { ...payload.error },
          status: true
        };
      return mapErrorHandle(errorHandle);
    }),
);

export const errorHandleReducer = (state: ErrorHandle, action: Action): ErrorHandle => {
  return featureReducer(state, action);
};


function mapErrorHandle(state: ErrorHandle): ErrorHandle {
  const uiError = getUiError(state);
  return {
    ...state,
    uiError: uiError,
  };
}

function getUiError(state: ErrorHandle): string {
  if (evalUseUpperCase(state?.error?.['code'], NoResults)) {
    return NoResultsTypes.NoResultsSearch;
  } else {
    const specificError = getSpecificError(state);
    if (specificError) {
      const errors = Object.values(specificError);
      const primaryError = errors?.[0];
      return primaryError?.includes(NO_PAST_DATES) ? NoResultsTypes.PastDates : NoResultsTypes.Error;
    } else {
      return NoResultsTypes.Error;
    }
  }
}

function getSpecificError(state: ErrorHandle): { [key: string]: string | string[] } {
  if (!isEmpty(state.error)) {
    switch (true) {
      case !isEmpty(state.error[ResultsErrorTypes.ModelState]):
        return state.error[ResultsErrorTypes.ModelState];
      case !isEmpty(state.error[ResultsErrorTypes.message]):
        return { [ResultsErrorTypes.message]: state.error[ResultsErrorTypes.message] };
      case !isEmpty(state.error[ResultsErrorTypes.Message]):
      case !isEmpty(state.error[ResultsErrorTypes.isTrusted]):
        const value = isEmpty(state.error[ResultsErrorTypes.isTrusted]) ? state.error[ResultsErrorTypes.Message] :
          ResultsErrorTypes.isTrusted;
        return {
          [ResultsErrorTypes.Message]: value
        };
      default:
        return null;
    }
  } else {
    return null;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function evalUseUpperCase(value: any, comparison: string): boolean {
  return !isEmpty(value) && !isEmpty(comparison) && typeof value === 'string' &&
    value.toUpperCase() === comparison.toUpperCase();
}
